import { produce } from 'immer'
import _set from 'lodash/set'
import React, { useEffect, useMemo, useReducer, useState } from 'react'
import { createPortal } from 'react-dom'
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import CustomTooltip from '../../../../_components/CustomTooltip'
import { ChrofyShowErrors } from '../../../common/Form'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import { Draggable, Droppable } from '../../DataMapper/extra/common'

export const VALUE_MAPPING_DND_UNSELECTED_COLUMN = '__CHROFY_VALUE_MAPPING_DND_UNSELECTED_COLUMN__'

/**
 * @param {any} e
 * @returns {string}
 */
function convertValuesToString(e) {
  if (e === null || e === undefined) return ''
  return String(e)
}

/**
 * @param {string | null | undefined} value
 * @returns {string | null}
 */
function normalizeSelectedColumnName(value) {
  if (value === null || value === undefined || value === '' || value === VALUE_MAPPING_DND_UNSELECTED_COLUMN)
    return null
  return String(value)
}

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizeSelectedColumnNameForForm(value) {
  return normalizeSelectedColumnName(value) ?? VALUE_MAPPING_DND_UNSELECTED_COLUMN
}

/**
 * Build form state from initial MappingInfo (DB → UI)
 *
 * @param {{
 *   ValueMapping?: {
 *     MappingFileType?: string
 *     MappingColumnName?: string
 *     MappingList?: { from?: string; to?: string; From?: string; To?: string }[]
 *   }
 *   valueMapping?: {
 *     mappingFileType?: string
 *     mappingColumnName?: string
 *     mappingList?: { from?: string; to?: string }[]
 *     MappingList?: { from?: string; to?: string; From?: string; To?: string }[]
 *   }
 * } | null} initial
 * @param {import('../extra/types').FileTypeInfo[]} fileTypes
 * @returns {{ mappingFileType: string; mappingColumnName: string; mappingList: { from: string; to: string }[] }}
 */
export function mappingInfoToFormData(initial, fileTypes) {
  const valueMapping = initial?.ValueMapping ?? initial?.valueMapping
  const mappingList =
    valueMapping?.MappingList ?? valueMapping?.mappingList ?? initial?.MappingList ?? initial?.mappingList ?? []
  const mappingFileType = valueMapping?.MappingFileType ?? valueMapping?.mappingFileType ?? ''
  const mappingColumnName = valueMapping?.MappingColumnName ?? valueMapping?.mappingColumnName ?? ''
  const list = Array.isArray(mappingList)
    ? mappingList.map((e) => ({
        from: e.from ?? e.From ?? e.from_value ?? e.fromValue ?? '',
        to: e.to ?? e.To ?? e.to_value ?? e.toValue ?? ''
      }))
    : []

  const firstFileType = fileTypes?.[0]?.mappingFileType ?? ''
  return {
    mappingFileType: mappingFileType || firstFileType,
    mappingColumnName: normalizeSelectedColumnNameForForm(mappingColumnName),
    mappingList: list.length > 0 ? list : []
  }
}

/**
 * Build MappingInfo from form state (UI → DB)
 *
 * @param {{ mappingFileType: string; mappingColumnName: string; mappingList: { from: string; to: string }[] }} formData
 * @returns {{
 *   MappingType: 'ValueMapping'
 *   ValueMapping: {
 *     MappingFileType: string
 *     MappingColumnName: string
 *     MappingList: { from: string; to: string }[]
 *   }
 * }}
 */
export function formDataToMappingInfo(formData) {
  const selectedColumnName = normalizeSelectedColumnName(formData.mappingColumnName)
  return {
    MappingType: 'ValueMapping',
    _valueMappingUiType: 'dnd',
    ValueMapping: {
      MappingFileType: formData.mappingFileType,
      MappingColumnName: selectedColumnName ?? '',
      MappingList: formData.mappingList ?? []
    }
  }
}

/**
 * Validate form (file type and column name are required)
 *
 * @param {{ mappingFileType: string; mappingColumnName: string; mappingList: { from: string; to: string }[] }} formData
 * @param {import('../extra/types').FileTypeInfo[]} [fileTypes]
 * @returns {Record<string, string[]> | null}
 */
export function validateValueMappingDndForm(formData, fileTypes = []) {
  const errors = /** @type {Record<string, string[]>} */ ({})
  if (!formData.mappingFileType?.trim()) {
    errors.mappingFileType = ['ファイル種別を選択してください']
  }
  const selectedColumnName = normalizeSelectedColumnName(formData.mappingColumnName)
  if (selectedColumnName === null) {
    errors.mappingColumnName = ['項目名を選択してください']
  }
  if (formData.mappingFileType?.trim() && selectedColumnName !== null) {
    const selectedFileType = fileTypes.find((e) => e.mappingFileType === formData.mappingFileType)
    const uniqueValues = Array.from(
      new Set((selectedFileType?.columnUniqueValues?.[selectedColumnName] ?? []).map((e) => convertValuesToString(e)))
    )
    const mappedValues = new Set((formData.mappingList ?? []).map((e) => convertValuesToString(e.from)))
    const remainingValues = uniqueValues.filter((e) => !mappedValues.has(e))
    if (remainingValues.length > 0) {
      errors.mappingList = [`未マッピングの値が${remainingValues.length}件あります。すべてマッピングしてください`]
    }
  }
  return Object.keys(errors).length > 0 ? errors : null
}

/**
 * @param {import('./ValueMappingDndForm').ValueMappingDndState} state
 * @param {{ type: string; payload: any }} action
 */
function valueMappingDndReducer(state, action) {
  return produce(state, (draft) => {
    switch (action.type) {
      case 'SET': {
        const { path, value } = action.payload
        _set(draft, path, value)
        break
      }
      default:
        break
    }
  })
}

/**
 * ValueMappingDndForm — 値変換D&D版モーダル（SR-071, SR-072, SR-089–SR-094, SR-102）
 * ファイル種別・項目名セレクト、D&Dエリア（システム値・ドロップエリア・設定済みファイル値×・利用可能なファイル値）、マッピング済みは非表示またはグレーアウト。
 *
 * @param {import('./ValueMappingDndForm').ValueMappingDndFormProps & {
 *   formData?: Record<string, unknown>
 *   errors?: Record<string, string[]>
 *   getPreview?: (params: unknown, payload: unknown) => Promise<unknown>
 *   previewRows?: unknown[]
 *   onPreviewLoad?: (rows: unknown[]) => void
 *   previewLoading?: boolean
 *   setPreviewLoading?: (v: boolean) => void
 * }} props
 */
export function ValueMappingDndForm(props) {
  const {
    onChange: _onChange,
    fileTypes = [],
    systemValues = [],
    mappingFileType,
    mappingColumnName,
    mappingList = [],
    formData: externalFormData,
    errors: externalErrors = {},
    getPreview,
    previewRows,
    onPreviewLoad,
    previewLoading,
    setPreviewLoading
  } = props

  // Use external formData if provided (controlled mode), otherwise use props
  const isControlled = externalFormData !== undefined
  const initialFormData = isControlled
    ? externalFormData
    : {
        mappingFileType: mappingFileType ?? '',
        mappingColumnName: normalizeSelectedColumnNameForForm(mappingColumnName),
        mappingList: mappingList
      }

  const [internalState, dispatch] = useReducer(valueMappingDndReducer, {
    mappingFileType: initialFormData.mappingFileType ?? '',
    mappingColumnName: normalizeSelectedColumnNameForForm(initialFormData.mappingColumnName),
    mappingSetting: (initialFormData.mappingList || []).map((e) => ({ From: e.from, To: e.to }))
  })

  // Use external formData in controlled mode, internal state otherwise
  const state = isControlled
    ? {
        mappingFileType: externalFormData.mappingFileType ?? '',
        mappingColumnName: normalizeSelectedColumnNameForForm(externalFormData.mappingColumnName),
        mappingSetting: (externalFormData.mappingList || []).map((e) => ({ From: e.from, To: e.to }))
      }
    : internalState

  const [errors, setErrors] = useState({})

  const dispatchSet = (path, value) => {
    if (isControlled) {
      // In controlled mode, update the parent's formData via onChange
      const newFormData = produce(externalFormData, (draft) => {
        if (path === 'mappingSetting') {
          // Convert { From, To } format to { from, to } format
          draft.mappingList = value.map((e) => ({ from: e.From, to: e.To }))
        } else {
          _set(draft, path, value)
        }
      })
      _onChange?.(newFormData, setErrors)
    } else {
      dispatch({ type: 'SET', payload: { path, value } })
    }
  }
  const [activeItem, setActiveItem] = useState(null)

  const { mappingFileType: fileType, mappingColumnName: columnName, mappingSetting } = state
  const selectedColumnName = normalizeSelectedColumnName(columnName)
  const displayErrors = useMemo(() => ({ ...errors, ...externalErrors }), [errors, externalErrors])

  const uniqueValues = useMemo(() => {
    if (!fileType || selectedColumnName === null) return []
    const ft = fileTypes.find((f) => f.mappingFileType === fileType)
    const col = ft?.columnUniqueValues?.[selectedColumnName]
    if (!Array.isArray(col)) return []
    return col.map((v) => convertValuesToString(v))
  }, [fileTypes, fileType, selectedColumnName])

  const chrofyValues = useMemo(() => [...systemValues], [systemValues])

  const uniqueItems = useMemo(() => {
    const values = Array.from(new Set(['', ...uniqueValues])).filter((x) => !mappingSetting.some((e) => e.From === x))
    const emptyIndex = values.indexOf('')
    if (emptyIndex !== -1) {
      const [empty] = values.splice(emptyIndex, 1)
      values.unshift(empty)
    }
    return values
  }, [uniqueValues, mappingSetting])

  const toMap = useMemo(() => {
    const map = new Map(chrofyValues.map((e) => [e, []]))
    map.forEach((_, key) => {
      const list = mappingSetting.filter((e) => e.To === key).map((e) => e.From)
      map.set(key, list)
    })
    return map
  }, [chrofyValues, mappingSetting])

  const handleDragStart = (e) => {
    setActiveItem(e.active.data.current?.value ?? null)
  }

  const handleDragEnd = (event) => {
    if (!event.over) {
      setActiveItem(null)
      return
    }
    const From = event.active.data.current?.value
    const To = event.over.data.current?.value
    if (From == null || To == null) {
      setActiveItem(null)
      return
    }
    const newMappingSetting = mappingSetting.filter((e) => e.From !== From)
    newMappingSetting.push({ From, To })
    dispatchSet('mappingSetting', newMappingSetting)
    notifyChange(newMappingSetting)
    setActiveItem(null)
  }

  const removeDataItem = (ev, value) => {
    ev?.stopPropagation()
    const newMappingSetting = mappingSetting.filter((e) => e.From !== value)
    dispatchSet('mappingSetting', newMappingSetting)
    notifyChange(newMappingSetting)
  }

  const notifyChange = (setting) => {
    // In controlled mode, onChange is already called by dispatchSet, so skip here
    if (isControlled) return

    const formData = {
      mappingFileType: state.mappingFileType,
      mappingColumnName: state.mappingColumnName,
      mappingList: setting.map((e) => ({ from: e.From, to: e.To }))
    }
    _onChange?.(formData, setErrors)
  }

  // In uncontrolled mode, notify parent when file type or column name changes
  useEffect(() => {
    if (!isControlled) {
      notifyChange(mappingSetting)
    }
  }, [state.mappingFileType, state.mappingColumnName, isControlled])

  // Notify parent on mount to provide setErrors callback.
  // In controlled mode, do not call onChange on mount so server-populated formData (set by OPEN) is not overwritten.
  useEffect(() => {
    if (!isControlled) {
      const formData = {
        mappingFileType: state.mappingFileType,
        mappingColumnName: state.mappingColumnName,
        mappingList: mappingSetting.map((e) => ({ from: e.From, to: e.To }))
      }
      _onChange?.(formData, setErrors)
    }
  }, [])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const renderEmptyValues = (e) => {
    const v = convertValuesToString(e)
    return v === '' ? '空' : v
  }

  const fileTypeOptions = useMemo(
    () => fileTypes.map((f) => ({ value: f.mappingFileType, label: f.mappingFileType })),
    [fileTypes]
  )
  const selectedFileType = fileTypes.find((f) => f.mappingFileType === fileType)
  const columnOptions = useMemo(() => {
    const headers = selectedFileType?.headers ?? []
    const headerOptions = headers
      .filter((h, index) => h !== '' && headers.indexOf(h) === index)
      .map((h) => ({ value: h, label: h }))
    return [{ value: VALUE_MAPPING_DND_UNSELECTED_COLUMN, label: '項目を選択' }, ...headerOptions]
  }, [selectedFileType])

  const handleFileTypeChange = (newFileType) => {
    if (newFileType === fileType) return
    if (isControlled) {
      const newFormData = produce(externalFormData, (draft) => {
        draft.mappingFileType = newFileType
        draft.mappingColumnName = VALUE_MAPPING_DND_UNSELECTED_COLUMN
        draft.mappingList = []
      })
      _onChange?.(newFormData, setErrors)
    } else {
      dispatchSet('mappingFileType', newFileType)
      dispatchSet('mappingColumnName', VALUE_MAPPING_DND_UNSELECTED_COLUMN)
      dispatchSet('mappingSetting', [])
    }
  }

  const handleColumnNameChange = (newColumnName) => {
    if (newColumnName === columnName) return
    if (isControlled) {
      const newFormData = produce(externalFormData, (draft) => {
        draft.mappingColumnName = newColumnName
        draft.mappingList = []
      })
      _onChange?.(newFormData, setErrors)
    } else {
      dispatchSet('mappingColumnName', newColumnName)
      dispatchSet('mappingSetting', [])
    }
  }

  return (
    <div className="text-h7">
      <div className="setting-table is-benchmark">
        <div className="form-table mb-4 border-t-0">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">ファイル種別を選択</label>
              <ChrofyCustomSelect
                id="value-dnd-modal-file-type"
                name="value-dnd-modal-file-type"
                value={fileType}
                onChange={(e) => handleFileTypeChange(e.target.value)}
                className="w-full"
                options={fileTypeOptions}
                disabled={false}
                hasNoSelect={true}
                searchable
                isError={Boolean(displayErrors.mappingFileType?.length)}
                data-testid="value-dnd-modal-file-type"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">項目選択</label>
              <ChrofyCustomSelect
                id="value-dnd-modal-column-name"
                name="value-dnd-modal-column-name"
                value={columnName}
                onChange={(e) => handleColumnNameChange(e.target.value)}
                className="w-full"
                options={columnOptions}
                disabled={!fileType}
                hasNoSelect={false}
                searchable
                isError={Boolean(displayErrors.mappingColumnName?.length)}
                data-testid="value-dnd-modal-column-name"
              />
              <ChrofyShowErrors field="mappingColumnName" errors={displayErrors} />
            </div>
          </div>
          <ChrofyShowErrors field="mappingFileType" errors={displayErrors} />
        </div>

        {chrofyValues.length > 0 && (
          <DndContext sensors={sensors} onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
            <div className="mt-5">
              <label className="block text-sm font-medium mb-2">値のマッピング設定（ドラッグアンドドロップ）</label>
              <ChrofyShowErrors field="mappingList" errors={displayErrors} />
              <div className="p-4 rounded-lg" style={{ background: '#f8f9fa' }}>
                <div className="grid grid-cols-2 gap-5" style={{ minHeight: 400 }}>
                  {/* 左側: システム項目の値（ドロップゾーン） */}
                  <div className="bg-white rounded-lg p-4" style={{ border: '1px solid #e0e5f2' }}>
                    <div className="font-semibold mb-3 text-sm" style={{ color: '#3150A5' }}>
                      システム項目の値
                    </div>
                    <div className="flex flex-col gap-2 overflow-y-auto" style={{ maxHeight: 350 }}>
                      {chrofyValues.map((chrofyValue, index) => {
                        const mappedValues = Array.from(new Set(toMap.get(chrofyValue) ?? []))
                        return (
                          <Droppable
                            key={index}
                            id={chrofyValue}
                            type="div"
                            value={chrofyValue}
                            className="rounded-md p-3 transition-all"
                            style={{ background: '#f8f9fa', border: '2px dashed #d0d7e5' }}
                          >
                            {() => (
                              <div className="flex flex-col gap-2">
                                <span className="font-semibold text-sm shrink-0" style={{ color: '#3150A5' }}>
                                  {chrofyValue}
                                </span>
                                <div className="flex flex-wrap gap-2">
                                  {mappedValues.map((e, key) => (
                                    <Draggable
                                      id={`${e}_x_${key}`}
                                      value={convertValuesToString(e)}
                                      useDragOverlay={true}
                                      key={key}
                                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs"
                                      style={{ background: '#e8f4f8' }}
                                    >
                                      {(listeners) => (
                                        <>
                                          <span className="select-none" style={{ color: '#1f2a44' }} {...listeners}>
                                            {renderEmptyValues(e)}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={(ev) => removeDataItem(ev, e)}
                                            aria-label="解除"
                                            className="text-xs leading-none"
                                            style={{
                                              padding: '0 4px',
                                              minWidth: 'auto',
                                              width: 'auto',
                                              height: 'auto'
                                            }}
                                          >
                                            ×
                                          </button>
                                        </>
                                      )}
                                    </Draggable>
                                  ))}
                                  {mappedValues.length === 0 && (
                                    <span className="text-xs" style={{ color: '#999' }}>
                                      ここにドロップしてください
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </Droppable>
                        )
                      })}
                    </div>
                  </div>

                  {/* 右側: ファイル項目の値（ドラッグ可能） */}
                  <div className="bg-white rounded-lg p-4" style={{ border: '1px solid #e0e5f2' }}>
                    <div className="font-semibold mb-3 text-sm" style={{ color: '#3150A5' }}>
                      ファイル項目の値
                    </div>
                    <div className="flex flex-col gap-2 overflow-y-auto pr-1" style={{ maxHeight: 350 }}>
                      {!fileType && (
                        <p className="text-xs text-center py-5" style={{ color: '#999' }}>
                          ファイル種別を選択してください
                        </p>
                      )}
                      {fileType && selectedColumnName === null && (
                        <p className="text-xs text-center py-5" style={{ color: '#999' }}>
                          項目を選択するとファイル項目の値が表示されます
                        </p>
                      )}
                      {fileType &&
                        selectedColumnName !== null &&
                        uniqueItems.map((e, idx) => (
                          <Draggable
                            key={idx}
                            id={`avail_${e}_${idx}`}
                            value={convertValuesToString(e)}
                            useDragOverlay={true}
                          >
                            <CustomTooltip title={renderEmptyValues(e)}>
                              {(ref) => (
                                <span
                                  ref={ref}
                                  className="py-1.5 px-2 inline-flex bg-blue-300/50 text-blue-500 select-none text-sm cursor-grab truncate w-full justify-center rounded-md"
                                >
                                  {renderEmptyValues(e)}
                                </span>
                              )}
                            </CustomTooltip>
                          </Draggable>
                        ))}
                      {fileType && selectedColumnName !== null && uniqueItems.length === 0 && (
                        <p className="text-xs text-center py-5" style={{ color: '#999' }}>
                          すべての値がマッピング済みです
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {createPortal(
              <DragOverlay>
                <span className="px-3 py-1 inline-flex bg-blue-300/50 text-blue-500 cursor-grab truncate min-w-[100px] text-sm rounded-md">
                  {activeItem != null ? renderEmptyValues(activeItem) : ''}
                </span>
              </DragOverlay>,
              document.body
            )}
          </DndContext>
        )}

        <hr />
      </div>
    </div>
  )
}

/**
 * @typedef {{
 *   mappingFileType: string
 *   mappingColumnName: string
 *   mappingSetting: { From: string; To: string }[]
 * }} ValueMappingDndState
 */

/**
 * @typedef {{
 *   fileTypes: import('../extra/types').FileTypeInfo[]
 *   systemValues: string[]
 *   mappingFileType?: string
 *   mappingColumnName?: string
 *   mappingList?: { from: string; to: string }[]
 *   errors?: Record<string, string[]>
 *   onChange?: (formData: {
 *     mappingFileType: string
 *     mappingColumnName: string
 *     mappingList: { from: string; to: string }[]
 *   }) => void
 * }} ValueMappingDndFormProps
 */

export default ValueMappingDndForm
